/* AdminDBBackupPage — v1.14.83.0
 * Backup MongoDB admin-only, download single-use con expiry 15 min.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Database, AlertTriangle, Download, Trash2, RefreshCcw, Copy, Terminal, Play,
  ShieldCheck, Clock, HardDrive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosCfg = { withCredentials: true };

function authHeaders() {
  try {
    const tok = JSON.parse(localStorage.getItem("colucci_admin_session") || "{}").token || "";
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  } catch { return {}; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtCountdown(expiresAt) {
  if (!expiresAt) return "—";
  const diff = Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
  if (diff === 0) return "SCADUTO";
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return `${m}m ${s}s`;
}

const CLI_SNIPPET = `# 1. Estrai il tar.gz
tar -xzf colucci_backup_*.tar.gz

# 2. Ripristina su Mongo locale (usa altro nome DB per non overwrite)
mongorestore --uri="mongodb://localhost:27017" \\
             --nsFrom="colucci_assicuratori.*" \\
             --nsTo="colucci_local.*" \\
             ./colucci_assicuratori/

# 3. Verifica integrità
sha256sum colucci_backup_*.tar.gz
# Confronta il valore SHA256 con quello mostrato nella dashboard`;

export default function AdminDBBackupPage() {
  const [items, setItems] = useState([]);
  const [lastCreated, setLastCreated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [, setTick] = useState(0);

  // Tick 1s per refresh countdown
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/db-backup/list`, { headers: authHeaders(), ...axiosCfg });
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error("Errore caricamento: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const doCreate = async () => {
    setShowConfirm(false);
    setCreating(true);
    try {
      const r = await axios.post(`${API}/admin/db-backup/create`, {}, { headers: authHeaders(), ...axiosCfg });
      setLastCreated(r.data);
      toast.success(`Backup creato: ${r.data.size_mb} MB in ${r.data.elapsed_seconds}s`);
      reload();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      if (err.response?.status === 429) {
        toast.error("Rate limit: " + msg);
      } else {
        toast.error("Errore creazione: " + msg);
      }
    } finally {
      setCreating(false);
    }
  };

  const doDownload = (backupId, filename) => {
    // Aggiungiamo il cookie via <a> con credentials="include" tramite fetch stream
    const url = `${API}/admin/db-backup/download/${backupId}`;
    // Fetch e download blob (per preservare header X-SHA256 e cookie auth)
    fetch(url, { credentials: "include", headers: authHeaders() })
      .then(async (res) => {
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`);
        }
        const sha = res.headers.get("x-sha256");
        const blob = await res.blob();
        const a = document.createElement("a");
        const url2 = URL.createObjectURL(blob);
        a.href = url2;
        a.download = filename || "colucci_backup.tar.gz";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url2);
        toast.success(`Download completo · SHA256: ${sha?.slice(0, 16)}…`);
        reload();
      })
      .catch((err) => toast.error("Download failed: " + err.message));
  };

  const doDelete = async (backupId) => {
    if (!window.confirm(`Eliminare backup ${backupId.slice(0,8)}…?`)) return;
    try {
      await axios.delete(`${API}/admin/db-backup/${backupId}`, { headers: authHeaders(), ...axiosCfg });
      toast.success("Backup eliminato");
      reload();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato");
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-db-backup-page">
      <header className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-2">Sistema · Backup Database</p>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#163321] flex items-center gap-3">
              <Database className="text-[#163321]" size={32} /> Backup Database
            </h1>
            <p className="text-sm text-[#4A5E51] mt-2 max-w-2xl">
              Dump completo MongoDB (tutte le collezioni), compresso in tar.gz.
              Download single-use, expiry 15 min, hard cap 3 backup simultanei, rate limit 1/ora.
            </p>
          </div>
          <Button onClick={reload} variant="outline" data-testid="db-backup-reload">
            <RefreshCcw size={16} className="mr-2" /> Aggiorna
          </Button>
        </div>
      </header>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="db-backup-warning">
        <div className="flex gap-3">
          <AlertTriangle className="text-amber-700 shrink-0" size={22} />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">Operazione riservata</p>
            <p>
              Ogni backup contiene <strong>TUTTI i dati del sito</strong> (pratiche D&amp;O/Cyber, richieste, utenti, log admin).
              Ogni creazione e download è <strong>tracciato</strong> nell&apos;audit log (severity=high) e collegato al tuo IP + email.
              Il file scade dopo <strong>15 minuti</strong> ed è scaricabile una sola volta.
            </p>
          </div>
        </div>
      </div>

      {/* Big create button + last created */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-[#E5E2DB] p-5 lg:col-span-1">
          <p className="text-xs uppercase tracking-wider text-[#4A5E51] mb-2">Crea nuovo backup</p>
          <p className="text-xs text-[#4A5E51] mb-3">Tempo stimato: 30-60 sec · Dimensione tipica: 5-50 MB</p>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={creating}
            className="w-full bg-[#163321] hover:bg-[#1d4a2e] text-white h-12 text-base font-semibold"
            data-testid="db-backup-create-btn">
            {creating ? (
              <><RefreshCcw size={18} className="mr-2 animate-spin" /> Backup in corso…</>
            ) : (
              <><Play size={18} className="mr-2" /> Crea Nuovo Backup Ora</>
            )}
          </Button>
        </div>

        {lastCreated ? (
          <div className="bg-emerald-50 rounded-2xl border-2 border-emerald-300 p-5 lg:col-span-2" data-testid="db-backup-last-created">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={18} className="text-emerald-700" />
              <p className="text-xs uppercase tracking-wider text-emerald-800 font-bold">Ultimo backup pronto</p>
              <span className="ml-auto text-xs text-emerald-700 flex items-center gap-1">
                <Clock size={12} /> Scade tra {fmtCountdown(lastCreated.expires_at)}
              </span>
            </div>
            <p className="text-sm text-emerald-900 mb-1"><strong>File:</strong> <code className="bg-white/80 px-1 py-0.5 rounded text-xs">{lastCreated.filename}</code></p>
            <p className="text-xs text-emerald-800 mb-1"><strong>SHA256:</strong> <code className="font-mono">{lastCreated.sha256.slice(0, 32)}…</code>
              <button onClick={() => copyToClipboard(lastCreated.sha256)} className="ml-2 underline" title="Copia SHA256"><Copy size={11} className="inline" /></button>
            </p>
            <p className="text-xs text-emerald-800 mb-3"><strong>Size:</strong> {lastCreated.size_mb} MB · Elapsed {lastCreated.elapsed_seconds}s</p>
            <Button
              onClick={() => doDownload(lastCreated.backup_id, lastCreated.filename)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="db-backup-download-last">
              <Download size={16} className="mr-2" /> Scarica ora (single-use)
            </Button>
          </div>
        ) : (
          <div className="bg-[#FCFCFA] rounded-2xl border border-[#E5E2DB] p-5 lg:col-span-2 flex items-center gap-3 text-sm text-[#4A5E51]">
            <HardDrive size={20} /> Nessun backup appena creato. Premi il pulsante a sinistra per generarne uno.
          </div>
        )}
      </div>

      {/* History table */}
      <div className="bg-white rounded-2xl border border-[#E5E2DB] p-4 mb-6">
        <h3 className="font-heading text-lg font-bold text-[#163321] mb-3">Ultimi backup (max 3 attivi + storico eliminati)</h3>
        {loading ? (
          <p className="text-sm text-[#4A5E51]">Caricamento…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#4A5E51] text-center py-6">Nessun backup ancora creato.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="db-backup-history-table">
              <thead className="text-xs uppercase text-[#4A5E51] bg-[#FCFCFA]">
                <tr>
                  <th className="text-left p-2">ID</th>
                  <th className="text-left p-2">Creato</th>
                  <th className="text-left p-2">Da</th>
                  <th className="text-right p-2">Size</th>
                  <th className="text-left p-2">SHA256</th>
                  <th className="text-center p-2">Downloads</th>
                  <th className="text-left p-2">Stato</th>
                  <th className="text-center p-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isDeleted = r.deleted;
                  const isExpired = !isDeleted && r.expires_at && new Date(r.expires_at) < new Date();
                  const canDownload = !isDeleted && !isExpired && (r.download_count || 0) === 0;
                  return (
                    <tr key={r.backup_id} className="border-t border-[#E5E2DB]">
                      <td className="p-2 font-mono text-xs">{r.backup_id.slice(0, 8)}…</td>
                      <td className="p-2 text-xs">{fmtDate(r.created_at)}</td>
                      <td className="p-2 text-xs truncate max-w-[160px]">{r.created_by}</td>
                      <td className="p-2 text-xs text-right">{(r.size_bytes / (1024*1024)).toFixed(2)} MB</td>
                      <td className="p-2 font-mono text-xs">{(r.sha256 || "").slice(0, 12)}…</td>
                      <td className="p-2 text-center text-xs">{r.download_count || 0}</td>
                      <td className="p-2 text-xs">
                        {isDeleted ? <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-600">Eliminato</span> :
                         isExpired ? <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">Scaduto</span> :
                         (r.download_count || 0) > 0 ? <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">Scaricato</span> :
                         <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Pronto · {fmtCountdown(r.expires_at)}</span>}
                      </td>
                      <td className="p-2 text-center">
                        {canDownload && (
                          <Button size="sm" variant="outline" onClick={() => doDownload(r.backup_id, r.filename)} data-testid={`download-${r.backup_id}`}>
                            <Download size={14} />
                          </Button>
                        )}
                        {!isDeleted && (
                          <Button size="sm" variant="ghost" onClick={() => doDelete(r.backup_id)} className="ml-1 text-red-600" data-testid={`delete-${r.backup_id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Usage guide */}
      <div className="bg-[#FCFCFA] rounded-2xl border border-[#E5E2DB] p-5">
        <h3 className="font-heading text-lg font-bold text-[#163321] mb-2 flex items-center gap-2">
          <Terminal size={18} /> Come usare il backup
        </h3>
        <p className="text-xs text-[#4A5E51] mb-3">
          Sul tuo Mac/PC con MongoDB installato + mongorestore in path:
        </p>
        <pre className="bg-zinc-900 text-emerald-300 text-xs p-4 rounded-lg overflow-x-auto whitespace-pre-wrap" data-testid="db-backup-cli-snippet">
{CLI_SNIPPET}
        </pre>
        <Button onClick={() => copyToClipboard(CLI_SNIPPET)} variant="outline" className="mt-3" size="sm">
          <Copy size={14} className="mr-2" /> Copia snippet CLI
        </Button>
        <p className="text-xs text-[#4A5E51] mt-3">
          <strong>Nota:</strong> Se non hai <code>mongodump/mongorestore</code>, scaricali da{" "}
          <a href="https://www.mongodb.com/try/download/database-tools" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">
            mongodb.com/try/download/database-tools
          </a> (Homebrew Mac: <code>brew install mongodb-database-tools</code>).
        </p>
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="db-backup-confirm-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="font-heading text-xl font-bold text-[#163321] mb-2">Conferma creazione backup</h3>
            <p className="text-sm text-[#4A5E51] mb-4">
              Sarà generato un dump completo di MongoDB e compresso in tar.gz.
              Tempo stimato <strong>30-60 sec</strong>. Il file scadrà dopo <strong>15 minuti</strong>.
              L&apos;operazione sarà loggata nell&apos;audit sicurezza.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowConfirm(false)} className="flex-1" data-testid="db-backup-confirm-cancel">
                Annulla
              </Button>
              <Button onClick={doCreate} className="flex-1 bg-[#163321]" data-testid="db-backup-confirm-ok">
                Conferma e crea
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
